import React from "react";
import "./EditRole.scss";
import * as coreApi from "../../shared/core-api-client";
import * as _ from "lodash";
import { Button, Dialog, DialogContent, TextField, DialogActions, Select, MenuItem, Chip, DialogTitleWithCloseButton, InlineInfoTooltip, InfoAlert, KeyValueEditor, Checkbox, LoadingContainer } from "../../shared/components";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { connect } from "react-redux";
import { showSuccess, showError } from "../../store/notifications/actions";
import { useSettings } from "../../shared/providers/SettingsProvider";

const schema = z.object({
    name: z.string({ required_error: "This field is required." }).min(1, { message: "This field is required." }),
    description: z.string({ required_error: "This field is required." }).min(1, { message: "This field is required." }),
    explicitTags: z.object({
        allowed: z.array(z.object({
            key: z.string(),
            value: z.string(),
        })),
        denied: z.array(z.object({
            key: z.string(),
            value: z.string(),
        })),
    }),
});
type FormValues = z.infer<typeof schema>;

interface EditRoleProps {
    open: boolean,
    setOpen: (open: boolean) => void,
    role?: coreApi.RoleListModel,
    showSuccess: typeof showSuccess,
    showError: typeof showError,
    onSuccess: () => void
}

function EditRole(props: EditRoleProps) {
    const { open, setOpen, role: _role, showSuccess, showError, onSuccess } = props;
    const defaultValues: FormValues = { name: "", description: "", explicitTags: { allowed: [], denied: [] } };
    const { register, handleSubmit, reset, setError, control } = useForm({ resolver: zodResolver(schema), defaultValues });
    const [formErrors, setFormErrors] = React.useState(null);
    const [role, setRole] = React.useState(null);
    const settings = useSettings();

    React.useEffect(() => {
        if (_role) {
            new coreApi.RolesClient(settings.coreApiUrl).getById(_role.id)
                .then(result => {
                    setRole(result);
                    reset(result);
                });
        }
        else {
            reset({ explicitTags: { allowed: [], denied: [] } });
        }
    }, [_role]);

    const closeDialog = () => {
        setOpen(false);
    }

    const attachFormErrors = (errorDictionary: object) => {
        _.forOwn(errorDictionary, (value, key) => {
            if (key === "non_field_errors") {
                setFormErrors(value[0]);
            }
            else {
                setError(_.camelCase(key) as keyof FormValues, { type: "server", message: value[0] });
            }
        });
    }

    const save = (formModel) => {
        const model = {
            domainName: settings.domainName,
            ...role,
            ...formModel,
        };

        const apiCall = role ?
            new coreApi.RolesClient(settings.coreApiUrl).update(role.id, model) :
            new coreApi.RolesClient(settings.coreApiUrl).create(model);

        (apiCall as Promise<any>)
            .then(() => {
                closeDialog();
                showSuccess(`Role has been ${role ? "updated" : "created"} successfully.`);
                onSuccess();
            })
            .catch((ex: coreApi.ApiException) => {
                if (ex.status === 400) {
                    attachFormErrors((ex.result as coreApi.ValidationErrorResponse).errors ?? {});
                }
                else {
                    showError(ex.response);
                }
            });
    };

    return (
        <Dialog
            id="edit-role-dialog"
            open={open}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    closeDialog();
                }
            }}
            aria-labelledby="form-dialog-title">
            <form onSubmit={handleSubmit(save)}>
                <DialogTitleWithCloseButton onClose={closeDialog} id="form-dialog-title">
                    {`${role ? "Edit " + role.name : "Create Role"}`}
                </DialogTitleWithCloseButton>
                <DialogContent className="vertical-form padding-top-0">
                    <div className="vertical-form-field">Name</div>
                    <Controller
                        name="name"
                        control={control}
                        render={({
                            field,
                            fieldState: { error }
                        }) => (
                            <TextField
                                {...field}
                                error={!!error}
                                helperText={error?.message}
                                variant="outlined"
                                fullWidth
                                autoFocus
                                title={role != null ? "This field cannot be changed once created." : null }
                                disabled={role != null}
                            />
                        )}
                    />

                    <div className="vertical-form-field">Description</div>
                    <Controller
                        name="description"
                        control={control}
                        render={({
                            field,
                            fieldState: { error }
                        }) => (
                            <TextField
                                {...field}
                                error={!!error}
                                helperText={error?.message}
                                variant="outlined"
                                fullWidth
                            />
                        )}
                    />

                    <div className="vertical-form-field">Allowed Tags
                        <InlineInfoTooltip
                            text="Allowed tags for this role.  These are cumulative with regards to the tags contained within other roles."
                        />
                    </div>
                    <Controller
                        name="explicitTags.allowed"
                        control={control}
                        render={({
                            field: { onChange, value },
                        }) => (
                            <KeyValueEditor
                                keyName="key"
                                updateEntries={onChange}
                                entries={value}
                            />
                        )}
                    />

                    <div className="vertical-form-field">Denied Tags
                        <InlineInfoTooltip
                            text="Denied tags for this role.  These are cumulative with regards to the tags contained within other roles."
                        />
                    </div>
                    <Controller
                        name="explicitTags.denied"
                        control={control}
                        render={({
                            field: { onChange, value },
                        }) => (
                            <KeyValueEditor
                                keyName="key"
                                updateEntries={onChange}
                                entries={value}
                            />
                        )}
                    />

                    {
                        formErrors &&
                        <div className="form-errors">
                            {formErrors}
                        </div>
                    }
                </DialogContent>
                <DialogActions>
                    <Button type="submit" variant="contained" color="primary">
                        {`${role?.id ? "Update" : "Create"} Role`}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default connect(
    null,
    { showSuccess, showError })(EditRole);